import { shouldFetchMlThemesFallback } from './themePerformanceHelpers';

export function mergeLazySuggestedIdsForMl(
  suggestedEvidenceIds: string[],
  assignedEvidences?: { _id?: { toString(): string } | string }[],
  filteredThemeEvidenceIds: string[] = [],
  ideaThemeEvidenceIds: string[] = []
): string[] {
  const assignedIds =
    assignedEvidences?.map((evidence) => evidence._id?.toString()).filter(Boolean) || [];
  let ids = [...new Set([...suggestedEvidenceIds, ...assignedIds])];
  if (filteredThemeEvidenceIds.length) {
    const excludeSet = new Set(filteredThemeEvidenceIds.map(String));
    ids = ids.filter((id) => !excludeSet.has(id));
  }
  if (ideaThemeEvidenceIds.length) {
    const excludeSet = new Set(ideaThemeEvidenceIds.map(String));
    ids = ids.filter((id) => !excludeSet.has(id));
  }
  return ids;
}

export async function resolveIdeaThemesWithLazyIds(options: {
  getThemes: () => Promise<{ themes?: { subThemes?: unknown[] } } | undefined>;
  getSuggestedEvidenceIds: () => Promise<{ evidenceIds: string[] }>;
  getSubThemes: (evidenceIds: string[]) => Promise<{ subThemes?: { subThemes?: unknown[] } }>;
  mergeEvidenceIds: (suggestedIds: string[]) => string[];
  evidencesReady?: boolean;
  hasAnyEvidences?: boolean;
}): Promise<{ themes: { subThemes: unknown[] } }> {
  const {
    getThemes,
    getSuggestedEvidenceIds,
    getSubThemes,
    mergeEvidenceIds,
    evidencesReady,
    hasAnyEvidences,
  } = options;

  try {
    const response = await getThemes();
    const subThemes = response?.themes?.subThemes;
    if (Array.isArray(subThemes) && subThemes.length > 0) {
      return { themes: { subThemes } };
    }
  } catch {
    // fall through to ML fallback
  }

  const evidenceGateCount = hasAnyEvidences ? 1 : 0;
  if (!shouldFetchMlThemesFallback(evidencesReady, evidenceGateCount, 0)) {
    return { themes: { subThemes: [] } };
  }

  const { evidenceIds: suggestedIds } = await getSuggestedEvidenceIds();
  const mergedIds = mergeEvidenceIds(suggestedIds);
  if (mergedIds.length === 0) {
    return { themes: { subThemes: [] } };
  }

  try {
    const mlResponse = await getSubThemes(mergedIds);
    return {
      themes: {
        subThemes: mlResponse?.subThemes?.subThemes || [],
      },
    };
  } catch {
    return { themes: { subThemes: [] } };
  }
}

export async function resolveRecalculatedThemesWithLazyIds(options: {
  getSuggestedEvidenceIds: () => Promise<{ evidenceIds: string[] }>;
  getSubThemes: (evidenceIds: string[]) => Promise<{ subThemes?: { subThemes?: unknown[] } }>;
  mergeEvidenceIds: (suggestedIds: string[]) => string[];
}): Promise<{ themes: { subThemes: unknown[] } }> {
  const { getSuggestedEvidenceIds, getSubThemes, mergeEvidenceIds } = options;

  const { evidenceIds: suggestedIds } = await getSuggestedEvidenceIds();
  const mergedIds = mergeEvidenceIds(suggestedIds);
  if (mergedIds.length === 0) {
    return { themes: { subThemes: [] } };
  }

  try {
    const mlResponse = await getSubThemes(mergedIds);
    return {
      themes: {
        subThemes: mlResponse?.subThemes?.subThemes || [],
      },
    };
  } catch {
    return { themes: { subThemes: [] } };
  }
}
